
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { App } from '@/modules/core/appstore/services/appData';
import { useAppStore } from '@/modules/core/appstore/services/store';

interface AppCardProps {
  app: App;
}

// Generate a consistent professional gradient based on app name
const getGradientFromName = (name: string) => {
  const gradients = [
    'from-slate-100 via-slate-50 to-gray-100',
    'from-blue-50 via-indigo-50 to-slate-100',
    'from-emerald-50 via-teal-50 to-cyan-50',
    'from-amber-50 via-orange-50 to-yellow-50',
    'from-violet-50 via-purple-50 to-fuchsia-50',
    'from-sky-50 via-blue-50 to-indigo-50',
    'from-rose-50 via-pink-50 to-red-50',
    'from-lime-50 via-green-50 to-emerald-50',
    'from-cyan-50 via-teal-50 to-blue-50',
    'from-fuchsia-50 via-pink-50 to-rose-50',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return gradients[hash % gradients.length];
};

// Get icon color based on app name
const getIconColorFromName = (name: string) => {
  const colors = [
    'text-slate-600',
    'text-blue-600',
    'text-emerald-600',
    'text-amber-600',
    'text-violet-600',
    'text-sky-600',
    'text-rose-600',
    'text-lime-600',
    'text-cyan-600',
    'text-fuchsia-600',
  ];
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export default function AppCard({ app }: AppCardProps) {
  const [imageError, setImageError] = useState(false);
  const { 
    setShowBuyModal, 
    setShowTrialModal, 
    setShowRequestModal,
    setSelectedApp, 
    installApp 
  } = useAppStore();

  const handleAction = () => {
    switch (app.status) {
      case 'Installed':
        // Launch app - use the actual launchUrl or show a message
        if (app.launchUrl && app.launchUrl !== '#') {
          window.open(app.launchUrl, '_blank');
        } else {
          alert(`Launching ${app.name}...`);
        }
        break;
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          setSelectedApp(app);
          setShowBuyModal(true);
        } else if (app.accessType === 'Free Trial') {
          setSelectedApp(app);
          setShowTrialModal(true);
        } else {
          // Free apps now also require a request
          setSelectedApp(app);
          setShowRequestModal(true);
        }
        break;
    }
  };

  const getActionButton = () => {
    switch (app.status) {
      case 'Installed':
        return (
          <button 
            onClick={handleAction}
            className="w-full bg-green-600 text-white py-2.5 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium whitespace-nowrap"
          >
            <i className="ri-play-line mr-1"></i>
            Launch
          </button>
        );
      case 'Not Installed':
        if (app.accessType === 'Paid') {
          return (
            <button 
              onClick={handleAction}
              className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line mr-1"></i>
              Buy
            </button>
          );
        } else if (app.accessType === 'Free Trial') {
          return (
            <button 
              onClick={handleAction}
              className="w-full bg-purple-600 text-white py-2.5 px-4 rounded-lg hover:bg-purple-700 transition-colors font-medium whitespace-nowrap"
            >
              <i className="ri-download-line mr-1"></i>
              Start Trial
            </button>
          );
        } else {
          return (
            <button 
              onClick={handleAction}
              className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
            >
              <i className="ri-download-line mr-1"></i>
              Install
            </button>
          );
        }
      default:
        return null;
    }
  };

  const getAccessTypeColor = () => {
    switch (app.accessType) {
      case 'Free':
        return 'bg-green-100 text-green-800';
      case 'Free Trial':
        return 'bg-purple-100 text-purple-800';
      case 'Paid':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-400 hover:shadow-lg transition-all duration-300 overflow-hidden group flex flex-col h-full">
      <div className="relative h-48 overflow-hidden">
        {!imageError && app.coverImage ? (
          <img
            src={app.coverImage}
            alt={`${app.name} cover`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${getGradientFromName(app.name)} flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
            <div className="text-center px-6">
              <div className={`w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-4 border border-gray-100`}>
                <i className={`ri-apps-2-line text-3xl ${getIconColorFromName(app.name)}`}></i>
              </div>
              <h4 className="text-gray-800 font-semibold text-base leading-tight line-clamp-2">
                {app.name}
              </h4>
              <p className="text-gray-500 text-xs mt-1">{app.category}</p>
            </div>
          </div>
        )}
        {!imageError && app.coverImage && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
        )}
        {app.isHighlighted && (
          <div className="absolute top-3 right-3">
            <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap shadow-sm">
              <i className="ri-star-fill mr-1"></i>
              Featured
            </span>
          </div>
        )}
        {app.status === 'Installed' && (
          <div className="absolute top-3 left-3">
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap shadow-sm">
              <i className="ri-check-line mr-1"></i>
              Installed
            </span>
          </div>
        )}
      </div>

      <div className="p-6 flex flex-col flex-1">
        <div className="flex-1">
          <Link to={`/app/appstore/app/${app.id}`} className="block">
            <h3 className="font-bold text-lg text-gray-900 mb-2 hover:text-blue-600 transition-colors line-clamp-1 group-hover:text-blue-600">
              {app.name}
            </h3>
          </Link>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2 leading-relaxed">
            {app.description}
          </p>
          
          <div className="flex items-center justify-between mb-3">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${getAccessTypeColor()}`}>
              {app.accessType}
            </span>
            <span className="text-xs text-gray-500 whitespace-nowrap bg-gray-50 px-2 py-1 rounded-full">{app.category}</span>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex items-center mr-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <i
                    key={star}
                    className={`ri-star-${star <= Math.floor(app.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                  ></i>
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {app.rating} ({app.reviewCount})
              </span>
            </div>
          </div>

          {app.status === 'Installed' && app.trialExpiresAt && (
            <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-100">
              <p className="text-xs text-orange-700 flex items-center">
                <i className="ri-time-line mr-2"></i>
                Trial expires: {new Date(app.trialExpiresAt).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-2 mt-auto">
          {getActionButton()}
          <Link
            to={`/app/appstore/app/${app.id}`}
            className="w-full border border-gray-200 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors font-medium text-center block whitespace-nowrap"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
}
